import InfoIcon from "@/assets/colorable/info_filled.svg?react";
import InstagramIcon from "@/assets/colorable/instagram.svg?react";
import TiktokIcon from "@/assets/colorable/tiktok.svg?react";
import UGCIcon from "@/assets/colorable/ugc.svg?react";
import UnverifyIcon from "@/assets/colorable/unverify.svg?react";
import CheckBox from "@/components/Common/CheckBox";
import type {PunishmentSerializer} from "@/generated/Punishment";
import clsxm from "@/lib/clsxm";
import {disciplinaryModalAtom} from "@/store/jotaiAtoms";
import type {TCampaignType} from "@/store/types";
import {useAtom} from "jotai";
import {memo, useMemo, type FC, type SVGProps} from "react";
import {useTranslation} from "react-i18next";
import {Tooltip} from "react-tooltip";
import {useUnmount} from "react-use";

const VerificationButton: FC = () => {
  const {t} = useTranslation();
  const [disciplinaryData, setDisciplinaryData] = useAtom(disciplinaryModalAtom);

  const options: {
    discipline?: PunishmentSerializer | null;
    Icon?: FC<SVGProps<SVGSVGElement>>;
    isUnverified?: boolean;
    key: TCampaignType;
  }[] = useMemo(
    () => [
      {
        discipline: disciplinaryData.instagramDiscipline,
        Icon: InstagramIcon,
        isUnverified: !disciplinaryData?.influencer.isInstagramVerified,
        key: "instagram",
      },
      {
        discipline: disciplinaryData.tiktokDiscipline,
        Icon: TiktokIcon,
        isUnverified: !disciplinaryData?.influencer.isTiktokVerified,
        key: "tiktok",
      },
      {
        discipline: disciplinaryData.ugcDiscipline,
        Icon: UGCIcon,
        isUnverified: !disciplinaryData?.influencer.isUgcVerified,
        key: "ugc",
      },
    ],
    [
      disciplinaryData?.influencer.isInstagramVerified,
      disciplinaryData?.influencer.isTiktokVerified,
      disciplinaryData?.influencer.isUgcVerified,
      disciplinaryData.instagramDiscipline,
      disciplinaryData.tiktokDiscipline,
      disciplinaryData.ugcDiscipline,
    ],
  );

  useUnmount(() => {
    setDisciplinaryData(draft => {
      draft.selectedUnverificationType = undefined;
    });
  });

  return (
    <div className="flex w-fit flex-col flex-wrap gap-2 sm:flex-row">
      {options.map(({discipline, Icon, isUnverified, key}) => {
        const isUnverifiedByDiscipline = !!discipline;
        const isSelected = disciplinaryData?.selectedUnverificationType === key;
        const isDisabled = isUnverified && !isUnverifiedByDiscipline;

        return (
          <div
            key={key}
            onClick={() => {
              if (!isDisabled) {
                setDisciplinaryData(draft => {
                  draft.selectedUnverificationType = isSelected ? undefined : key;
                  draft.selectedDiscipline = isSelected ? undefined : isUnverifiedByDiscipline ? discipline : undefined;
                });
              }
            }}
            className={clsxm(
              "border-neutral-gray-dark flex cursor-pointer flex-row items-center gap-2 rounded-md border-1 bg-white px-3 py-2.5 text-sm font-semibold transition-all",
              isSelected ? "border-primary-dark-solid bg-primary-light-solid" : "border-primary-light-solid bg-white",
              isDisabled && "bg-neutral-white-dark border-neutral-grey-light cursor-not-allowed",
              isUnverifiedByDiscipline && "text-danger-raspberry",
            )}>
            {isDisabled ? (
              <>
                <InfoIcon data-tooltip-id={`type-switcher-${key}`} className="text-primary-dark-solid size-3.5" />
                <Tooltip
                  className="border-neutral-white-dark text-neutral-grey-dark border p-3"
                  classNameArrow="border border-t-white border-l-white border-b-neutral-white-dark border-r-neutral-white-dark"
                  id={`type-switcher-${key}`}
                  place="bottom"
                  variant="light"
                  delayShow={200}>
                  {t("influencer_discipline:not_verified", {platform: t(key)})}
                </Tooltip>
              </>
            ) : (
              <CheckBox
                isChecked={isSelected}
                className={isSelected ? "border-none" : "border-primary-sky rounded-[2.4px]"}
              />
            )}
            {Icon && <Icon className="size-4 shrink-0" />}
            {t(key)}
            {isUnverifiedByDiscipline && <UnverifyIcon />}
          </div>
        );
      })}
    </div>
  );
};

export default memo(VerificationButton);
